import { Leaf, Zap, ShieldCheck, Recycle } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const CHAPTERS = [
  {
    num: "01",
    icon: Leaf,
    title: "Nutrient Density",
    copy: "Microgreens pack up to 40× the vitamins and antioxidants of mature greens — vitamins C, E, K and beta-carotene in every serving of microgreen protein.",
  },
  {
    num: "02",
    icon: Zap,
    title: "Clean Energy",
    copy: "Phytonutrients and natural nitrates support blood flow and endurance — sports nutrition that works with your body, not against it.",
  },
  {
    num: "03",
    icon: ShieldCheck,
    title: "Recovery & Immunity",
    copy: "Polyphenols and sulforaphane help fight exercise-induced oxidative stress, so you recover faster and train more often.",
  },
  {
    num: "04",
    icon: Recycle,
    title: "Sustainable by Design",
    copy: "Grown hydroponically in 7–10 days with 95% less water than conventional crops. Functional nutrition with a footprint we're proud of.",
  },
];

const WhyMicrogreens = () => (
  <section id="why-microgreens" data-testid="why-microgreens-section" className="border-t border-white/10 py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <div className="mb-16 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-[#7ED321]">Why Microgreens</p>
          <h2 className="font-display text-4xl uppercase leading-[0.95] text-white sm:text-5xl lg:text-6xl">
            Small greens. <span className="text-outline">Serious</span> science.
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <p className="max-w-md text-sm leading-relaxed text-[#A6A66A] md:text-base">
            We harvest young seedlings at peak nutrition and fold them into every bar and scoop.
            This is the manifesto behind every ZENTRA product.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        <Reveal className="md:col-span-12 lg:col-span-7">
          <div className="relative flex h-full min-h-[380px] flex-col justify-between overflow-hidden border border-white/10 bg-[#121212] p-8 lg:p-12">
            <div className="spotlight absolute inset-0" aria-hidden="true" />
            <img
              src="/assets/img-sprout.png"
              alt="Microgreen sprouts — the heart of ZENTRA microgreen protein"
              loading="lazy"
              className="pointer-events-none absolute -right-16 bottom-0 hidden w-56 opacity-50 sm:block lg:-right-10 lg:w-80"
            />
            <span className="relative z-10 font-display text-8xl text-[#7ED321] lg:text-9xl" data-testid="microgreens-stat">40×</span>
            <img
              src="/assets/img-sprout.png"
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="ml-auto w-36 opacity-80 sm:hidden"
            />
            <div className="relative z-10 max-w-sm">
              <h3 className="font-display text-2xl uppercase tracking-wide text-white lg:text-3xl">
                More nutrients than mature greens
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#A6A66A]">
                Peer-reviewed studies show microgreens concentrate vitamins, minerals and
                antioxidants far beyond their adult counterparts. We dehydrate them within
                hours of harvest to lock it all in.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:col-span-12 lg:col-span-5 lg:grid-cols-1 xl:grid-cols-2">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={0.08 * i} className="h-full">
              <div
                data-testid={`microgreens-chapter-${c.num}`}
                className="group relative flex h-full flex-col justify-between overflow-hidden border border-white/10 bg-[#121212] p-7 transition-colors duration-500 hover:border-[#7ED321]/50"
              >
                <span className="pointer-events-none absolute -right-2 -top-4 font-display text-7xl text-white/[0.05] transition-colors duration-500 group-hover:text-[#7ED321]/10" aria-hidden="true">
                  {c.num}
                </span>
                <c.icon className="h-7 w-7 text-[#7ED321]" />
                <div className="mt-8">
                  <h3 className="font-display text-xl uppercase tracking-wide text-white">{c.title}</h3>
                  <p className="mt-3 text-xs leading-relaxed text-[#A6A66A]">{c.copy}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default WhyMicrogreens;
